import React, { useState } from 'react';
import { VStack, IconButton, Text, useToast } from '@chakra-ui/react';
import { FiThumbsUp, FiThumbsDown, FiShare2, FiHeart } from 'react-icons/fi';
import IconWrapper from '../IconWrapper';
import { useNavigate } from 'react-router-dom';

interface SocialActionsProps {
  likes: number;
  dislikes: number;
  isLiked: boolean;
  isDisliked: boolean;
  onLike: () => void;
  onDislike: () => void;
  onShare: () => void;
  onDonate: () => void;
  creatorName: string;
  creatorId: string;
  contentId: string;
  contentTitle: string;
}

const SocialActions: React.FC<SocialActionsProps> = ({
  likes,
  dislikes,
  isLiked,
  isDisliked,
  onLike,
  onDislike,
  onShare,
  onDonate,
  creatorName,
  creatorId,
  contentId,
  contentTitle
}) => {
  const navigate = useNavigate();
  const toast = useToast();

  const formatCount = (count: number): string => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
    return count.toString();
  };

  const handleLike = () => {
    onLike();
    toast({
      title: isLiked ? 'Like removed' : 'Liked!',
      status: 'success',
      duration: 1000,
      isClosable: false,
    });
  };

  const handleDislike = () => {
    onDislike();
    toast({
      title: isDisliked ? 'Dislike removed' : 'Disliked',
      status: 'info',
      duration: 1000,
      isClosable: false,
    });
  };

  const handleShare = () => {
    onShare();
    toast({
      title: 'Share link copied!',
      status: 'success',
      duration: 2000,
      isClosable: false,
    });
  };

  const handleDonate = () => {
    onDonate();
  };

  return (
    <VStack
      position="absolute"
      right={{ base: 2, md: 4 }}
      bottom={20}
      zIndex={20}
      spacing={6}
      align="center"
    >
      <VStack spacing={1} align="center">
        <IconButton
          aria-label="Like"
          icon={<IconWrapper icon={FiThumbsUp} size={24} />}
          size="lg"
          bg="transparent"
          color={isLiked ? 'red.400' : 'white'}
          _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
          onClick={handleLike}
        />
        <Text color="white" fontSize="sm" fontWeight="medium">
          {formatCount(likes)}
        </Text>
      </VStack>

      <VStack spacing={1} align="center">
        <IconButton
          aria-label="Dislike"
          icon={<IconWrapper icon={FiThumbsDown} size={24} />}
          size="lg"
          bg="transparent"
          color={isDisliked ? 'red.400' : 'white'}
          _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
          onClick={handleDislike}
        />
        <Text color="white" fontSize="sm" fontWeight="medium">
          Dislike
        </Text>
      </VStack>

      <VStack spacing={1} align="center">
        <IconButton
          aria-label="Share"
          icon={<IconWrapper icon={FiShare2} size={24} />}
          size="lg"
          bg="transparent"
          color="white"
          _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
          onClick={handleShare}
        />
        <Text color="white" fontSize="sm" fontWeight="medium">
          Share
        </Text>
      </VStack>

      <VStack spacing={1} align="center">
        <IconButton
          aria-label="Donate"
          icon={<IconWrapper icon={FiHeart} size={24} />}
          size="lg"
          bg="transparent"
          color="white"
          _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
          onClick={() => navigate(`/donation/${creatorId}/${contentId}`, {
            state: { creatorName, contentTitle }
          })}
        />
        <Text color="white" fontSize="sm" fontWeight="medium">
          Donate
        </Text>
      </VStack>

    </VStack>
  );
};

export default SocialActions;
